import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { changeSidebar } from '~/actions';
import {
  BILLING_ROUTE,
  BUSINESS_SETTINGS_ROUTE,
  CLIENT_LIST_ROUTE,
  DASHBOARD_ROUTE,
  EMAIL_LIST_ROUTE,
  ESTIMATE_LIST_ROUTE,
  INVOICE_LIST_ROUTE,
} from '~/constants/routes';
import { useBoolean } from '~/hooks/useBoolean';
import ClientsIcon from '~/icons/ClientsIcon';
import DashboardIcon from '~/icons/DashboardIcon';
import EmailIcon from '~/icons/EmailIcon';
import EstimateIcon from '~/icons/EstimateIcon';
import InvoiceIcon from '~/icons/InvoiceIcon';
import SettingsIcon from '~/icons/SettingsIcon';
import UpgradeIcon from '~/icons/UpgradeIcon';
import { analytics, ANALYTICS_EVENTS } from '~/services/analyticsService';
import { RootState } from '~/types';
import { getScreenNameFromPath } from '~/utils/analytics';
import { useIsDesktop } from '../hooks/useIsDesktop';
import Feedback from './Feedback';
import Gap from './Gap';
import Text from './Text';
import TextButton from './TextButton';
import UserProfile from './UserProfile';

const SidebarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px 20px 20px 20px;
  height: 100%;
`;

const LinkWrapper = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SidebarLink = styled(Link, {
  shouldForwardProp: prop => prop !== 'active',
})<{ active?: boolean }>`
  text-decoration: none;
  padding: 10px;
  border-radius: 8px;
  color: var(--neut3);
  font-size: 18px;
  font-weight: 500;
  line-height: 30px;
  text-align: left;
  transition:
    background 0.3s ease,
    color 0.3s ease;

  &:hover {
    background: ${props => (props.active ? 'var(--prim4)' : 'var(--neut7)')};
  }

  ${props =>
    props.active &&
    css`
      background: var(--prim4);
      color: var(--white);
    `}
`;

const FeedbackContainer = styled.div`
  border: 1px solid var(--border1);
  border-radius: 8px;
  padding: 16px;
`;

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const GoldenCTA = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 4px;
  background-color: #fffbeb;
  border: 1px solid #fed7aa;
  color: #92400e;
  text-decoration: none;
  font-weight: 600;
  font-size: 12px;
  transition: background-color 0.3s ease;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 215, 0, 0.25) 50%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 3s ease-in-out infinite;
    pointer-events: none;
  }

  &:hover {
    background-color: #fef3c7;
  }
`;

const routes = [
  {
    path: DASHBOARD_ROUTE,
    pattern: /^\/private\/dashboard$/,
    label: 'Dashboard',
    icon: DashboardIcon,
    buttonId: 'dashboard_link',
  },
  {
    path: ESTIMATE_LIST_ROUTE,
    pattern: /^\/private\/estimate.*/,
    label: 'Estimates',
    icon: EstimateIcon,
    buttonId: 'estimates_link',
  },
  {
    path: INVOICE_LIST_ROUTE,
    pattern: /^\/private\/invoices.*/,
    label: 'Invoices',
    icon: InvoiceIcon,
    buttonId: 'invoices_link',
  },
  {
    path: CLIENT_LIST_ROUTE,
    pattern: /^\/private\/clients.*/,
    label: 'Clients',
    icon: ClientsIcon,
    buttonId: 'clients_link',
  },
  {
    path: EMAIL_LIST_ROUTE,
    pattern: /^\/private\/emails.*/,
    label: 'Emails',
    icon: EmailIcon,
    buttonId: 'emails_link',
  },

  {
    path: BUSINESS_SETTINGS_ROUTE,
    pattern: /^\/private\/business$/,
    label: 'Business Settings',
    icon: SettingsIcon,
    buttonId: 'settings_link',
  },
];

const Sidebar = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isDesktop = useIsDesktop();

  const { currentPlan } = useSelector((state: RootState) => state.user);
  const [isFeedbackVisible, feedbackOptions] = useBoolean();

  function onCloseSidebarModal() {
    dispatch(changeSidebar(false));
  }

  function handleLinkClick(linkName: string, buttonId: string) {
    const currentScreen = getScreenNameFromPath(location.pathname);
    analytics.capture(ANALYTICS_EVENTS.LINK_CLICKED, {
      screen_name: currentScreen,
      link_name: linkName,
      button_id: buttonId,
      section: 'Nav',
    });
    onCloseSidebarModal();
  }

  function handleUpgradeClick() {
    const currentScreen = getScreenNameFromPath(location.pathname);
    analytics.capture(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      screen_name: currentScreen,
      button_name: 'UPGRADE PLAN',
      button_id: 'upgrade_cta_nav',
      section: 'Nav',
    });
    onCloseSidebarModal();
  }

  return (
    <SidebarWrapper>
      <Feedback
        isVisible={isFeedbackVisible}
        handleClose={() => {
          feedbackOptions.off();
        }}
      />

      <div>
        {isDesktop && (
          <div>
            <Text size="md" className="bold">
              CreateMyInvoice
            </Text>
            <Gap t={8} />
            {currentPlan !== 'infinity' && (
              <GoldenCTA to={BILLING_ROUTE} onClick={handleUpgradeClick} className="center">
                <UpgradeIcon size={16} stroke="#92400e" />
                UPGRADE PLAN
              </GoldenCTA>
            )}
          </div>
        )}

        <Gap t={68} />

        <LinkWrapper>
          {routes.map(eachRoute => {
            const active = eachRoute.pattern.test(location.pathname);
            return (
              <SidebarLink
                className="align-center"
                key={eachRoute.path}
                to={eachRoute.path}
                active={active}
                onClick={() => handleLinkClick(eachRoute.label, eachRoute.buttonId)}
              >
                <eachRoute.icon fill={active ? 'var(--white)' : '#7B7986'} />
                <Gap r={14} />
                {eachRoute.label}
              </SidebarLink>
            );
          })}
        </LinkWrapper>
      </div>

      <div>
        <FeedbackContainer>
          <Text align="center">
            Got a minute to share your thoughts? The good, the bad anything helps!
          </Text>
          <Gap t={20} />
          <TextButton
            onClick={() => {
              feedbackOptions.on();
            }}
            size="full"
          >
            Send us your feedback
          </TextButton>
        </FeedbackContainer>

        <Gap t={40} />

        <UserProfile />
      </div>
    </SidebarWrapper>
  );
};

export default Sidebar;
